import type * as React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { MagnifyingGlass as SearchIcon } from "@phosphor-icons/react/MagnifyingGlass";

import type { QueryErrorState, QueryField } from "../types";

export interface QueryErrorMessagesProps {
	/** Error state information */
	errorState: QueryErrorState;
	/** Query fields for filter display */
	queryFields?: QueryField[];
	/** Callback when retry button is clicked */
	onRetry?: () => void;
	/** Disabled state */
	disabled?: boolean;
}

export function QueryErrorMessages({
	errorState,
	queryFields = [],
	onRetry,
	disabled = false,
}: QueryErrorMessagesProps): React.JSX.Element {
	const formatFilterValue = (field: QueryField, value: any): string => {
		if (!value) return "Not set";

		switch (field.type) {
			case "text": {
				if (Array.isArray(value) && value.length > 0) {
					return value.join(", ");
				}
				return "Not set";
			}
			case "numeric": {
				if (Array.isArray(value) && value.length === 2) {
					return `${value[0]} - ${value[1]}`;
				}
				return "Not set";
			}
			case "date": {
				if (
					value &&
					typeof value === "object" &&
					(value.startDate || value.endDate)
				) {
					const start = value.startDate || "Any";
					const end = value.endDate || "Any";
					return `${start} to ${end}`;
				}
				return "Not set";
			}
			default: {
				return "Not set";
			}
		}
	};

	const getAppliedFiltersText = (): React.ReactNode[] => {
		const items: React.ReactNode[] = [];

		// Add field filters
		for (const field of queryFields) {
			const value = errorState.appliedFilters[field.id];
			const formattedValue = formatFilterValue(field, value);

			if (formattedValue !== "Not set") {
				items.push(
					<Typography
						key={field.id}
						variant="body2"
						component="li"
						sx={{ fontSize: "0.8rem" }}>
						<strong>{field.displayName}:</strong> {formattedValue}
					</Typography>,
				);
			}
		}

		// Add spatial selection
		if (errorState.spatialSelection) {
			const spatialLabels = {
				point: "Point selection",
				rectangle: "Rectangle selection",
				circle: "Circle selection",
				polygon: "Polygon selection",
				all: "All features",
			};

			const label =
				spatialLabels[
					errorState.spatialSelection as keyof typeof spatialLabels
				] || errorState.spatialSelection;
			items.push(
				<Typography
					key="spatial"
					variant="body2"
					component="li"
					sx={{ fontSize: "0.8rem" }}>
					<strong>Spatial Selection:</strong> {label}
				</Typography>,
			);
		}

		if (items.length === 0) {
			items.push(
				<Typography
					key="none"
					variant="body2"
					component="li"
					sx={{ fontSize: "0.8rem" }}>
					No specific filters applied
				</Typography>,
			);
		}

		return items;
	};

	const getSeverity = () => {
		switch (errorState.type) {
			case "no_results": {
				return "info";
			}
			case "too_many_results": {
				return "warning";
			}
			case "query_failed":
			default: {
				return "error";
			}
		}
	};

	const getTitle = () => {
		switch (errorState.type) {
			case "no_results": {
				return "No Results Found";
			}
			case "too_many_results": {
				return "Too Many Results";
			}
			case "query_failed":
			default: {
				return "Query Failed";
			}
		}
	};

	const getActionButton = () => {
		if (errorState.type === "query_failed") {
			return (
				<Button
					variant="outlined"
					size="small"
					onClick={onRetry}
					disabled={disabled}
					startIcon={<SearchIcon size={16} />}>
					Retry Query
				</Button>
			);
		}
		return null;
	};

	return (
		<Alert
			severity={getSeverity()}
			sx={{ mt: 2 }}
			action={getActionButton()}>
			<Stack spacing={1}>
				<Box>
					<Typography
						variant="body2"
						sx={{ fontWeight: "bold", mb: 0.5 }}>
						{getTitle()}
					</Typography>
					<Typography
						variant="body2"
						color="text.secondary">
						{errorState.message}
					</Typography>
				</Box>

				{/* Applied Filters Summary */}
				<Box>
					<Typography
						variant="caption"
						sx={{ fontWeight: "medium", mb: 0.5, textTransform: "uppercase" }}>
						Current Search Criteria:
					</Typography>
					<Box
						component="ul"
						sx={{ m: 0, pl: 2, "& li": { mb: 0.25 } }}>
						{getAppliedFiltersText()}
					</Box>
				</Box>

				{/* Suggestions */}
				{errorState.suggestions && errorState.suggestions.length > 0 && (
					<Box>
						<Typography
							variant="caption"
							sx={{
								fontWeight: "medium",
								mb: 0.5,
								textTransform: "uppercase",
							}}>
							Suggestions:
						</Typography>
						<Box
							component="ul"
							sx={{ m: 0, pl: 2, "& li": { mb: 0.25 } }}>
							{errorState.suggestions.map((suggestion, index) => (
								<Typography
									key={index}
									variant="body2"
									component="li"
									sx={{ fontSize: "0.8rem" }}>
									{suggestion}
								</Typography>
							))}
						</Box>
					</Box>
				)}
			</Stack>
		</Alert>
	);
}
